import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import DateRangeSelector from "../../../components/DateRangeSelector";

interface DoctorWiseData {
  _id?: string;
  paidAmount?: number;
  createdBy?: any;
}

interface DoctorWiseChartProps {
  data: DoctorWiseData[];
  dateRange: string;
  fromDate: string;
  toDate: string;
  onDateRangeChange: (value: string) => void;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
}

const DoctorWiseChart: React.FC<DoctorWiseChartProps> = ({
  data,
  dateRange,
  fromDate,
  toDate,
  onDateRangeChange,
  onFromDateChange,
  onToDateChange,
}) => {
  // Process data to group by doctor and sum their paid amounts
  const processedData = React.useMemo(() => {
    const doctorMap = new Map<string, number>();
    data.forEach(item => {
      const doctorName = item.createdBy.fullName || item.createdBy.firstName;
      // Safely access payment details with fallback values
      const paidAmount = item.paidAmount ?? 0;
      doctorMap.set(doctorName, (doctorMap.get(doctorName) || 0) + paidAmount);
    });

    const result = Array.from(doctorMap.entries())
      .map(([doctorName, totalAmount]) => ({
        doctorName,
        paidAmount: totalAmount,
      }))
      .sort((a, b) => b.paidAmount - a.paidAmount); // Sort by highest amount
    return result;
  }, [data]);

  const maxAmount =
    processedData.length > 0
      ? Math.max(...processedData.map(d => d.paidAmount), 1000)
      : 1000;
  console.log(processedData, "fghjkl");

  // Custom tooltip to show formatted currency
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800">{`Doctor: ${label}`}</p>
          <p className="text-blue-600">
            {`Paid Amount: ${payload[0].value.toLocaleString()}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-700">
          User-wise Lab Revenue{" "}
          {dateRange === "custom"
            ? `between ${fromDate} and ${toDate}`
            : `${dateRange}`}
        </h3>
        <DateRangeSelector
          preset="lab"
          compact
          dateRange={dateRange}
          fromDate={fromDate}
          toDate={toDate}
          onDateRangeChange={onDateRangeChange}
          onFromDateChange={onFromDateChange}
          onToDateChange={onToDateChange}
        />
      </div>

      {processedData.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <p>No data available for the selected date range</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={processedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="doctorName"
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
            />
            <YAxis
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={value => `${value.toLocaleString()}`}
              domain={[0, maxAmount * 1.1]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="paidAmount"
              fill="#0ea5e9"
              radius={[4, 4, 0, 0]}
              className="hover:opacity-80 transition-opacity"
            />
          </BarChart>
        </ResponsiveContainer>
      )}

      {/* {processedData.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          <p>
            Total Doctors: {processedData.length} | Total Revenue: ₹
            {processedData
              .reduce((sum, item) => sum + item.paidAmount, 0)
              .toLocaleString()}
          </p>
        </div>
      )} */}
    </div>
  );
};

export default DoctorWiseChart;
